import { useState, useCallback, MutableRefObject, Dispatch, SetStateAction } from 'react';
import { IAssistantScript, PlaybackPolicy, ProgressEntry } from '@interfaces/assistantScript';
import { LinearScriptPlayer } from './scriptPlayer/linearScriptPlayer';
import { ScriptNodeJSON } from './scriptPlayer/intefraces';
import { log } from './Debug';
import { globals } from './globals';
import { isLandingTrigger, isAITrigger } from './providerUtils';
import { regionNameToCamelCase } from './Regions';
import { SCREENS } from '@core/assistant/interfaces';
import { StartFlowFn } from './useScriptFlow';

export type UseRegionHandlerParams = {
  linearScriptPlayerRef: MutableRefObject<LinearScriptPlayer | undefined>;
  isAudioPlaybackActiveRef: MutableRefObject<boolean>;
  scriptsRef: MutableRefObject<IAssistantScript[]>;
  progressState: MutableRefObject<string>;
  sessionProgressRef: MutableRefObject<Record<string, ProgressEntry[]>>;
  enqueuedScriptIdRef: MutableRefObject<string | null>;
  pendingRegionClearRef: MutableRefObject<boolean>;
  stopPlaybackRef: MutableRefObject<() => void>;
  startFlowRef: MutableRefObject<StartFlowFn>;
  setOpen: (open: boolean) => void;
  // True when "Ask Me Anything" is enabled for the current event. Read via a ref
  // because handleRegionChange is a stable callback (empty dep array).
  isAskMeAnythingEnabledRef: MutableRefObject<boolean>;
  // The real "on join" region name (or the synthetic 'landing' fallback) —
  // read via a ref for the same reason as isAskMeAnythingEnabledRef.
  joinTriggerNameRef: MutableRefObject<string>;
  // Child region name -> parent region name, for regions with a parentTrigger
  // (e.g. exhibition booths grouped under an exhibition hall) — read via a
  // ref for the same reason as isAskMeAnythingEnabledRef.
  parentTriggerMapRef: MutableRefObject<Record<string, string>>;
  setActiveScreen: Dispatch<SetStateAction<SCREENS>>;
  setNavigationHistory: Dispatch<SetStateAction<SCREENS[]>>;
  setIsScriptCompleted: Dispatch<SetStateAction<boolean>>;
  setIsScriptTransitioning: Dispatch<SetStateAction<boolean>>;
  setIsScriptQuizActive: Dispatch<SetStateAction<boolean>>;
  setIsDisplayUserFeedback: Dispatch<SetStateAction<boolean>>;
  setCanUserSkip: Dispatch<SetStateAction<boolean>>;
  setPendingScriptStart: Dispatch<
    SetStateAction<{ step: number; area: string; title: string } | null>
  >;
};

export const useRegionHandler = ({
  linearScriptPlayerRef,
  isAudioPlaybackActiveRef,
  scriptsRef,
  progressState,
  sessionProgressRef,
  enqueuedScriptIdRef,
  pendingRegionClearRef,
  stopPlaybackRef,
  startFlowRef,
  setOpen,
  isAskMeAnythingEnabledRef,
  joinTriggerNameRef,
  parentTriggerMapRef,
  setActiveScreen,
  setNavigationHistory,
  setIsScriptCompleted,
  setIsScriptTransitioning,
  setIsScriptQuizActive,
  setIsDisplayUserFeedback,
  setCanUserSkip,
  setPendingScriptStart,
}: UseRegionHandlerParams) => {
  const [simulatedRegionOverride, setSimulatedRegionOverride] = useState<
    string | undefined
  >(undefined);

  const handleRegionChange = useCallback((incomingRegion: string) => {
    log('[region] changed to', incomingRegion);

    // Any new region change supersedes a pending bridged stop — re-evaluate from
    // scratch. The defer branch below re-arms it if we hit the same condition.
    pendingRegionClearRef.current = false;

    const playerState = linearScriptPlayerRef.current?.getCurrentState();
    if (playerState?.isRunning && playerState.name === incomingRegion) {
      // Deliberately-started playback (e.g. Visited Areas "resume/replay/select
      // step", which teleports then calls startFlow with bypassPlaybackPolicy)
      // sets isRunning synchronously, before its audio fetch resolves. The
      // teleport's own UE region-enter echo can land in that window — without
      // this check it would be mistaken for a fresh walk-in and restart the
      // script from scratch (or, for ui_click, stop the in-flight fetch and
      // surface a redundant "Take a tour" prompt on top of it).
      log(
        '[region] script already running for this region — skipping restart',
      );
      return;
    }

    const findScriptForRegion = (regionName: string) =>
      scriptsRef.current.find((x) => {
        const scriptTrigger = (
          Array.isArray(x.scriptTrigger) ? x.scriptTrigger : [x.scriptTrigger]
        ).map((trigger) =>
          trigger.includes('_') && !trigger.startsWith('AITrigger_')
            ? regionNameToCamelCase(trigger)
            : trigger,
        );
        return scriptTrigger.includes(regionName);
      });

    // A script bound to a "linked area" is scoped to the PARENT region's name
    // ([parentName, ...spots]) — a child region (e.g. an exhibition booth) has
    // no trigger token of its own. Fall back to the parent's name so entering
    // any child still surfaces the parent's script.
    const parentRegionName = incomingRegion
      ? parentTriggerMapRef.current[incomingRegion]
      : undefined;
    const scriptData =
      findScriptForRegion(incomingRegion as string) ??
      (parentRegionName ? findScriptForRegion(parentRegionName) : undefined);

    log('[region] script found for region', {
      scriptId: scriptData?.scriptId ?? 'none',
      triggerType: scriptData?.triggerType,
      interruptMode: scriptData?.interruptMode,
      bridgeRegionGaps: scriptData?.bridgeRegionGaps ?? false,
      canUserSkip: scriptData?.canUserSkip,
    });

    const defaultPlaybackPolicy = isAITrigger(scriptData?.scriptTrigger ?? '')
      ? PlaybackPolicy.ALWAYS
      : PlaybackPolicy.ONCE_SESSION;
    const effectivePlaybackPolicy =
      scriptData?.playbackPolicy ?? defaultPlaybackPolicy;

    if (scriptData) {
      if (!scriptData.interruptMode) {
        scriptData.interruptMode = 'finish_step';
      }

      const parsedNodes: ScriptNodeJSON[] = JSON.parse(
        scriptData.scriptData || '[]',
      );
      const lastNodeIndex = parsedNodes.length - 1;
      const isCompletedIn = (state: Record<string, ProgressEntry[]>) => {
        const entries = state[incomingRegion];
        return (
          parsedNodes.length > 0 &&
          !!entries?.length &&
          (entries.at(-1)?.[0] ?? 0) >= lastNodeIndex
        );
      };

      const isCompleted =
        effectivePlaybackPolicy === PlaybackPolicy.ONCE_SESSION ||
        effectivePlaybackPolicy === PlaybackPolicy.FIRST_TIME
          ? isCompletedIn(sessionProgressRef.current)
          : isCompletedIn(JSON.parse(progressState.current || '{}'));

      if (isCompleted && effectivePlaybackPolicy !== PlaybackPolicy.ALWAYS) {
        const currentPlayerState =
          linearScriptPlayerRef.current?.getCurrentState();
        const protectingDifferentRegion =
          (currentPlayerState?.isRunning || isAudioPlaybackActiveRef.current) &&
          currentPlayerState?.name !== incomingRegion;
        if (protectingDifferentRegion) {
          log(
            '[region] script already completed — updating region silently, not stopping audio',
            incomingRegion,
          );
          globals.currentRegion = incomingRegion;
          return;
        }
        log(
          '[region] script already completed — fall through to normal flow',
          incomingRegion,
        );
      }
    }

    if (!scriptData) {
      log(
        '[region] no script for region — stopping current player',
        incomingRegion,
      );

      const currentPlayerName =
        linearScriptPlayerRef.current?.getCurrentState().name;

      // Only suppress the reset for the synthetic 'landing' trigger, which is
      // never a real UE region — an empty incoming region while it plays just
      // means region-tracking hasn't caught up yet, not that the user left.
      // A real isStartingPoint region (e.g. "Lobby") IS a walkable trigger
      // volume, so an empty incoming region there means the user genuinely
      // exited it and the UI should reset like any other region.
      const isSyntheticJoinTrigger =
        joinTriggerNameRef.current.toLowerCase() === 'landing';

      if (
        !incomingRegion &&
        isSyntheticJoinTrigger &&
        isLandingTrigger(currentPlayerName ?? '', joinTriggerNameRef.current)
      ) {
        globals.currentRegion = incomingRegion;
        return;
      }

      const currentScript = currentPlayerName
        ? scriptsRef.current.find((x) => {
            const triggers = (
              Array.isArray(x.scriptTrigger)
                ? x.scriptTrigger
                : [x.scriptTrigger]
            ).map((t) =>
              t.includes('_') && !t.startsWith('AITrigger_')
                ? regionNameToCamelCase(t)
                : t,
            );
            return triggers.includes(currentPlayerName);
          })
        : null;

      const wasAudioPlaying = isAudioPlaybackActiveRef.current;

      // bridgeRegionGaps: the user walked into an empty region while a
      // finish_step script is still talking. Instead of cutting the step, defer
      // the stop + UI reset until the current step's audio ends naturally (see
      // useAssistantAudio.audioEndCallback). If a new scripted region arrives
      // first, handleRegionChange clears the flag at the top and the normal
      // finish_step enqueue path takes over.
      const isRunning =
        linearScriptPlayerRef.current?.getCurrentState().isRunning ?? false;
      const shouldBridgeGap =
        wasAudioPlaying &&
        isRunning &&
        currentScript?.bridgeRegionGaps === true &&
        currentScript?.interruptMode === 'finish_step';

      if (shouldBridgeGap) {
        log(
          '[region] no script — bridgeRegionGaps: deferring stop until current step ends',
          {
            scriptId: currentScript?.scriptId,
            interruptMode: currentScript?.interruptMode,
          },
        );
        pendingRegionClearRef.current = true;
        globals.currentRegion = incomingRegion;
        return;
      }

      if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
        linearScriptPlayerRef.current?.stop();
      }
      stopPlaybackRef.current();
      enqueuedScriptIdRef.current = null;
      globals.currentRegion = incomingRegion;

      if (
        !incomingRegion ||
        !wasAudioPlaying ||
        currentScript?.triggerType === 'ui_click'
      ) {
        log('[region] no script — resetting UI to default state');
        globals.activeScreenGlob = SCREENS.DEFAULT;
        setActiveScreen(SCREENS.DEFAULT);
        setNavigationHistory([SCREENS.DEFAULT]);
        setIsScriptCompleted(false);
        setIsScriptTransitioning(false);
        setIsScriptQuizActive(false);
        setIsDisplayUserFeedback(false);
        setCanUserSkip(false);
        setPendingScriptStart(null);

        // No script for this region and free-form Q&A is off — the assistant
        // has nothing to offer, so close the panel instead of showing an empty
        // default screen.
        if (!isAskMeAnythingEnabledRef.current) {
          setOpen(false);
        }
      }
      return;
    }

    const { triggerType, interruptMode } = scriptData;
    const isPlayerRunning =
      linearScriptPlayerRef.current?.getCurrentState().isRunning ?? false;

    // Case 1: ui_click / custom_spot — never auto-start, show the button.
    // This is checked BEFORE the current-script interrupt gate so an incoming
    // ui_click always surfaces its button and stops the current script, even
    // when the current script is non_interruptible (e.g. another ui_click tour
    // already playing). The user can then start the new ui_click at any moment.
    if (triggerType === 'ui_click' || triggerType === 'custom_spot') {
      log(
        '[region] ui_click/custom_spot — stopping current script, setting pendingScriptStart',
        {
          scriptId: scriptData.scriptId,
          playbackPolicy: scriptData.playbackPolicy,
          triggerType,
          interruptMode,
        },
      );
      if (isPlayerRunning) {
        linearScriptPlayerRef.current?.stop();
      }
      stopPlaybackRef.current();
      enqueuedScriptIdRef.current = null;
      globals.currentRegion = incomingRegion;
      startFlowRef.current('', { scriptId: scriptData.scriptId });
      return;
    }

    if (isPlayerRunning) {
      const currentPlayerName =
        linearScriptPlayerRef.current?.getCurrentState().name;
      const currentScriptData = currentPlayerName
        ? scriptsRef.current.find((x) => {
            const triggers = (
              Array.isArray(x.scriptTrigger)
                ? x.scriptTrigger
                : [x.scriptTrigger]
            ).map((t) => (t.includes('_') ? regionNameToCamelCase(t) : t));
            return triggers.includes(currentPlayerName);
          })
        : null;

      if (currentScriptData?.interruptMode === 'non_interruptible') {
        log(
          '[region] non_interruptible (current script) — incoming script will not start or enqueue:',
          scriptData.scriptId,
        );
        globals.currentRegion = incomingRegion;
        return;
      }

      if (
        currentScriptData?.interruptMode === 'finish_step' &&
        effectivePlaybackPolicy !== PlaybackPolicy.ALWAYS
      ) {
        if (!isAudioPlaybackActiveRef.current) {
          log(
            '[region] finish_step (current script) — audio already ended, starting enqueued script immediately',
            {
              scriptId: scriptData.scriptId,
              playbackPolicy: scriptData.playbackPolicy,
              triggerType: scriptData.triggerType,
              interruptMode: scriptData.interruptMode,
            },
          );
          const scriptId = scriptData.scriptId;
          setIsScriptCompleted(false);
          setPendingScriptStart(null);
          setIsScriptTransitioning(true);
          globals.currentRegion = incomingRegion;
          setTimeout(() => {
            if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
              linearScriptPlayerRef.current?.stop();
            }
            stopPlaybackRef.current();
            setIsScriptTransitioning(false);
            startFlowRef.current('', { scriptId });
          }, 500);
          return;
        }
        log(
          '[region] finish_step (current script) — enqueueing incoming script to start after current step ends',
          {
            scriptId: scriptData.scriptId,
            playbackPolicy: scriptData.playbackPolicy,
            triggerType: scriptData.triggerType,
            interruptMode: scriptData.interruptMode,
          },
        );
        enqueuedScriptIdRef.current = scriptData.scriptId;
        globals.currentRegion = incomingRegion;
        return;
      }
    }

    // Case 3: non_interruptible — do not interrupt the currently running script
    if (interruptMode === 'non_interruptible' && isPlayerRunning) {
      log(
        '[region] non_interruptible — current script is running, not interrupting. New region script will not start:',
        scriptData.scriptId,
      );
      globals.currentRegion = incomingRegion;
      return;
    }

    // Case 2: hard_cut (or area_entry with no running player) — stop immediately and start new script
    log('[region] hard_cut — stopping current script and starting new one', {
      scriptId: scriptData.scriptId,
      playbackPolicy: scriptData.playbackPolicy,
      triggerType,
      interruptMode,
    });
    if (isPlayerRunning) {
      linearScriptPlayerRef.current?.stop();
    }
    stopPlaybackRef.current();
    enqueuedScriptIdRef.current = null;
    globals.currentRegion = incomingRegion;
    startFlowRef.current('', { scriptId: scriptData.scriptId });
  }, []);

  const simulateRegionChange = useCallback((regionName: string | undefined) => {
    setSimulatedRegionOverride(regionName);
  }, []);

  return {
    simulatedRegionOverride,
    handleRegionChange,
    simulateRegionChange,
  };
};
